import { Dispatch, SetStateAction, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import * as AuthService from '../../api/services/Auth';
import { getSettings } from '../../api/services/Settings';
import { axiosClient } from '../../api/utils/provider';
import { clearAuthData, getImpersonationExpiresAt, getRules, removeRules } from '../../utils/helpers/storage';
import { getImpersonate } from '../impersonation';

export const AUTH_USER_QUERY_KEY = 'authUser';
export const AUTH_FEATURES_QUERY_KEY = 'authUserFeatures';

export const AUTH_IDENTITY_QUERY_KEY = 'authUserIdentity';

export const AUTH_IMPERSONATION_QUERY_KEY = 'authImpersonation';

const authRoutes = ['/organizers/welcome', '/forgotPassword', '/organizers/password/edit'];

interface UserSettingsResult {
  accountFeatures: UserFeatures;
  userSettings: AccountInfo;
}

// An unauthenticated profile is an expected state; resolve to null without throwing.
export const fetchAuthUser = async (): Promise<User | null> => {
  const response = await AuthService.getUserProfile();

  if (response?.type !== 'success') {
    clearAuthData();

    return null;
  }

  return { rules: getRules(), ...response.data };
};

const fetchAuthUserSettings = async (): Promise<UserSettingsResult | null> => {
  const response = await getSettings();

  if (response?.type !== 'success') {
    return null;
  }

  const settings = response.data.enterprise;

  settings.sendEmailDailyReport = response.data.notifications?.absentRiders?.enabled ? 'Yes' : 'No';
  settings.dailyReportEmailList = response.data.notifications?.absentRiders?.options?.emails?.join(', ') || '';

  return {
    accountFeatures: response.data.accountFeatures,
    userSettings: { ...response.data.enterprise, ...settings },
  };
};

export const fetchAuthIdentity = async (): Promise<Identity | null> => {
  const response = await AuthService.getUserIdentity();

  if (response?.type !== 'success') {
    return null;
  }

  return response.data;
};

export const useAuthQuery = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const isLoginRoute = location.pathname === '/login';
  const isAuthRoute = authRoutes.includes(location.pathname);

  axiosClient.defaults.withCredentials = true;

  // The cache shares this startup handshake across every auth subscriber.
  const impersonationQuery = useQuery({
    queryKey: [AUTH_IMPERSONATION_QUERY_KEY],
    queryFn: async () => {
      const wasImpersonating = !!getImpersonationExpiresAt();

      if (isLoginRoute) {
        return !!(await getImpersonate()) || wasImpersonating;
      }
      removeRules();

      return wasImpersonating;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnMount: false,
  });
  const impersonationChecked = impersonationQuery.isSuccess;
  const isImpersonating = impersonationQuery.data ?? false;
  const shouldFetchProfile = !isAuthRoute && impersonationChecked && (!isLoginRoute || isImpersonating);

  const profileQuery = useQuery({
    queryKey: [AUTH_USER_QUERY_KEY],
    queryFn: fetchAuthUser,
    enabled: shouldFetchProfile,
    retry: false,
    refetchOnMount: false,
  });

  const user = profileQuery.data ?? undefined;

  const featuresQuery = useQuery({
    queryKey: [AUTH_FEATURES_QUERY_KEY],
    queryFn: fetchAuthUserSettings,
    enabled: !!user,
    retry: false,
    refetchOnMount: false,
  });

  const identityQuery = useQuery({
    queryKey: [AUTH_IDENTITY_QUERY_KEY],
    queryFn: fetchAuthIdentity,
    enabled: !!user,
    retry: false,
    refetchOnMount: false,
  });

  const setUserSettings: Dispatch<SetStateAction<AccountInfo | undefined>> = useCallback(
    (update) => {
      queryClient.setQueryData<UserSettingsResult | null | undefined>([AUTH_FEATURES_QUERY_KEY], (previous) => {
        if (!previous) {
          return previous;
        }

        const nextUserSettings =
          typeof update === 'function'
            ? (update as (prev?: AccountInfo) => AccountInfo)(previous.userSettings)
            : update;

        return { ...previous, userSettings: nextUserSettings ?? previous.userSettings };
      });
    },
    [queryClient]
  );

  const loading = shouldFetchProfile
    ? profileQuery.isLoading || (!!user && (featuresQuery.isFetching || identityQuery.isFetching))
    : !impersonationChecked;

  return {
    user,
    userFeatures: featuresQuery.data?.accountFeatures,
    userSettings: featuresQuery.data?.userSettings,
    setUserSettings,
    userIdentity: identityQuery.data ?? undefined,
    loading,
    isImpersonating,
    enableOnsiteStaff: featuresQuery.data?.userSettings.enableOnsiteStaff === 'Yes',
    isOnsiteStaffUser: user?.roles.includes('onsite_staff') || false,
    isAdminOrOrganizerUser: user?.roles.includes('account_admin') || user?.roles.includes('trip_organizer') || false,
  };
};
